import { getPlannerInvestorOptions, getThreadState } from '@/api/weave-x-agents';
import { updateConversationAdditionalContextJson } from '@/store/features/canvas/canvasSlice';
import type { AdditionalContextJson } from '@/common/components/wavvest-planning/types';

export const FIRM_DATA_PROFILE_ACCESS_LOADING_MESSAGE =
  'Investor profile access is still loading. Please try again in a moment.';

export type PlannerInvestorProfileSelection = {
  investorId: string;
  displayName: string;
  firmId?: string;
};

export type PlannerProfileSelectionResolution =
  | {
      status: 'ready';
      candidate: PlannerInvestorProfileSelection | null;
      selection: PlannerInvestorProfileSelection | null;
    }
  | {
      status: 'blocked';
      candidate: PlannerInvestorProfileSelection | null;
      level: 'warning' | 'error';
      message: string;
    };

type PlannerProfileDispatch = (
  action: ReturnType<typeof updateConversationAdditionalContextJson>,
) => unknown;

export function parsePlannerInvestorProfileSelection(
  value: string | undefined,
): PlannerInvestorProfileSelection | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<PlannerInvestorProfileSelection>;
    const investorId = typeof parsed.investorId === 'string' ? parsed.investorId.trim() : '';
    const displayName = typeof parsed.displayName === 'string' ? parsed.displayName.trim() : '';
    const firmId = typeof parsed.firmId === 'string' ? parsed.firmId.trim() : '';
    if (!investorId) return null;
    return {
      investorId,
      displayName: displayName || `Investor ${investorId.slice(0, 8)}`,
      ...(firmId ? { firmId } : {}),
    };
  } catch {
    return null;
  }
}

export function plannerInvestorProfileSelectionFromSources(
  plannerInvestorProfile: string | undefined,
): PlannerInvestorProfileSelection | null {
  // A selected investor can assign a session, but only the explicit profile
  // context should opt a planner request into firm profile data.
  return parsePlannerInvestorProfileSelection(plannerInvestorProfile);
}

export function userClientIdFromStateUser(user: unknown): string {
  if (!user || typeof user !== 'object') {
    return '';
  }

  const { client_id, clientId } = user as { client_id?: unknown; clientId?: unknown };
  const rawClientId = typeof client_id === 'string' && client_id.trim() ? client_id : clientId;
  return typeof rawClientId === 'string' && rawClientId.trim() ? rawClientId.trim() : '';
}

function objectRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function threadGraphIdFromState(threadState: unknown): string | null {
  const record = objectRecord(threadState);
  const metadata = objectRecord(record?.metadata);
  const graphId = metadata?.graph_id;
  return typeof graphId === 'string' && graphId.trim() ? graphId.trim() : null;
}

const PLANNER_PROFILE_SESSION_GRAPHS = new Set([
  'canvas_agent',
  'weave_orchestrator',
  'orchestrator_v4',
]);

export async function shouldStartNewPlannerProfileSession(
  sessionId: string,
): Promise<boolean | null> {
  try {
    const threadState = await getThreadState(sessionId);
    const graphId = threadGraphIdFromState(threadState);
    return !graphId || !PLANNER_PROFILE_SESSION_GRAPHS.has(graphId);
  } catch (error) {
    console.warn('Failed to verify planner profile session graph:', error);
    return null;
  }
}

export function plannerProfileSessionOptions(selection: PlannerInvestorProfileSelection | null) {
  if (!selection) return undefined;
  return {
    clientId: selection.firmId,
    investorId: selection.investorId,
  };
}

async function hydratePlannerInvestorProfileSelection(
  selection: PlannerInvestorProfileSelection | null,
  additionalContext: AdditionalContextJson | undefined,
  dispatch: PlannerProfileDispatch,
  user: unknown,
): Promise<PlannerInvestorProfileSelection | null> {
  if (!selection || selection.firmId) {
    return selection;
  }

  const firmScopeId = userClientIdFromStateUser(user);
  if (!firmScopeId) {
    return selection;
  }

  try {
    const options = await getPlannerInvestorOptions(firmScopeId);
    const match = options.find((option) => option.investorId === selection.investorId);
    if (!match?.firmId || !match.plannerSelectable) {
      return selection;
    }

    const hydratedSelection: PlannerInvestorProfileSelection = {
      firmId: match.firmId,
      investorId: match.investorId,
      displayName: match.displayName || selection.displayName,
    };

    dispatch(
      updateConversationAdditionalContextJson({
        ...(additionalContext || {}),
        plannerInvestorProfile: JSON.stringify(hydratedSelection),
      }),
    );

    return hydratedSelection;
  } catch (error) {
    console.warn('Failed to hydrate planner investor profile selection:', error);
    return selection;
  }
}

export async function resolvePlannerProfileSelectionForSession({
  firmDataEnabled,
  firmDataLoading,
  plannerInvestorProfile,
  additionalContextJson,
  dispatch,
  user,
}: {
  firmDataEnabled: boolean;
  firmDataLoading: boolean;
  plannerInvestorProfile: string | undefined;
  additionalContextJson: AdditionalContextJson | undefined;
  dispatch: PlannerProfileDispatch;
  user: unknown;
}): Promise<PlannerProfileSelectionResolution> {
  const plannerProfileCandidate =
    plannerInvestorProfileSelectionFromSources(plannerInvestorProfile);
  if (firmDataLoading && plannerProfileCandidate) {
    return {
      status: 'blocked',
      candidate: plannerProfileCandidate,
      level: 'warning',
      message: FIRM_DATA_PROFILE_ACCESS_LOADING_MESSAGE,
    };
  }

  const selection = firmDataEnabled
    ? await hydratePlannerInvestorProfileSelection(
        plannerProfileCandidate,
        additionalContextJson,
        dispatch,
        user,
      )
    : null;
  if (selection && !selection.firmId) {
    return {
      status: 'blocked',
      candidate: plannerProfileCandidate,
      level: 'error',
      message: 'Unable to load the selected investor profile. Please re-select it.',
    };
  }

  return { status: 'ready', candidate: plannerProfileCandidate, selection };
}
